import { SPOUSE_INVITE_EXPIRY_DAYS } from "@nfp-tracker/utils";
import { supabase } from "../integrations/supabase/client";

export type SpouseInviteAcceptReason =
  | "not_authenticated"
  | "not_found"
  | "canceled"
  | "already_accepted"
  | "expired"
  | "email_mismatch"
  | "unknown";

export const SPOUSE_INVITE_ACCEPT_MESSAGES: Record<SpouseInviteAcceptReason, string> = {
  not_authenticated: "Please sign in again to accept this invite.",
  not_found: "We couldn't find that invite anymore.",
  canceled: "That invite was canceled. Ask your spouse to send a new one.",
  already_accepted: "That invite was already accepted.",
  expired: "That invite has expired. Ask your spouse to resend it from Settings.",
  email_mismatch: "That invite was sent to a different email address than the one you signed in with.",
  unknown: "We couldn't accept that invite. Please try again.",
};

export class SpouseInviteAcceptError extends Error {
  reason: SpouseInviteAcceptReason;

  constructor(reason: SpouseInviteAcceptReason) {
    super(SPOUSE_INVITE_ACCEPT_MESSAGES[reason]);
    this.name = "SpouseInviteAcceptError";
    this.reason = reason;
  }
}

export const buildInviteExpiry = (from: Date = new Date()): Date => {
  const expiresAt = new Date(from);
  expiresAt.setDate(expiresAt.getDate() + SPOUSE_INVITE_EXPIRY_DAYS);
  return expiresAt;
};

export const isInviteExpired = (expiresAt: string | null | undefined): boolean =>
  Boolean(expiresAt) && new Date(expiresAt as string).getTime() <= Date.now();

// Accepts via the reason-returning RPC so failures carry a cause instead of a
// bare false. Throws SpouseInviteAcceptError with a user-facing message.
export const acceptSpouseInvite = async (inviteId: string): Promise<void> => {
  const { data, error } = await supabase.rpc("accept_link_invite_with_reason", {
    invite_id: inviteId,
  });

  if (error) {
    throw error;
  }

  const result = data as { accepted?: boolean; reason?: string } | null;

  if (!result?.accepted) {
    const reason = (result?.reason ?? "unknown") as SpouseInviteAcceptReason;
    throw new SpouseInviteAcceptError(
      reason in SPOUSE_INVITE_ACCEPT_MESSAGES ? reason : "unknown"
    );
  }
};

export const getInviteAcceptErrorMessage = (error: unknown): string | undefined =>
  error instanceof SpouseInviteAcceptError ? error.message : undefined;

// Resending an invite also pushes its expiry out, so a stale invite becomes
// usable again instead of dead-ending the invitee.
export const extendInviteExpiry = async (inviteId: string): Promise<string> => {
  const expiresAt = buildInviteExpiry().toISOString();
  const { error } = await supabase
    .from("link_invites")
    .update({ expires_at: expiresAt })
    .eq("id", inviteId);

  if (error) {
    throw error;
  }

  return expiresAt;
};
